/**
 * Définition déclarative de la visite guidée : chapitres → étapes.
 *
 * Une étape cible un élément par sélecteur CSS (convention `data-tour="clé"`),
 * peut imposer une route (le moteur navigue avant d'afficher la bulle), et porte
 * un texte pédagogique. Sans `target`, la bulle s'affiche centrée (accueil /
 * conclusion). Les étapes « à toi de jouer » invitent à agir puis « Suivant ».
 *
 * T-B livre le 1er chapitre (« Prise en main ») de bout en bout ; les autres
 * n'ont pas encore d'étapes (rédigées en T-C) et apparaissent « À venir » dans
 * le menu.
 */

export type Placement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

/**
 * Déclencheur d'avance automatique d'une étape « à toi de jouer » : dès que
 * l'action attendue est réalisée, le moteur passe seul à l'étape suivante
 * (sans attendre le clic « Suivant »).
 *  - `routeChange` : l'utilisateur a navigué (ex. il a ouvert un plan / projet).
 *  - `appear`      : un élément apparaît dans le DOM (ex. il a ouvert un onglet).
 *  - `modalClosed` : une modale ouverte pendant l'étape s'est refermée (ex. il a
 *                    déposé une idée / créé une tâche via le formulaire).
 */
export type AdvanceOn =
  | { type: 'routeChange' }
  | { type: 'appear'; selector: string }
  | { type: 'modalClosed' };

export interface TourStep {
  /** Sélecteur CSS de la cible (souvent `[data-tour="clé"]`). Absent = bulle centrée. */
  target?: string;
  /** Route à ouvrir avant l'étape (le moteur y navigue au besoin). */
  route?: string;
  title: string;
  /** Corps pédagogique (texte simple ; le rendu échappe le HTML). */
  body: string;
  /** Placement préféré de la bulle par rapport à la cible. */
  placement?: Placement;
  /** Consigne « à toi de jouer » affichée en encart sous le corps. */
  hint?: string;
  /** Avance automatiquement l'étape quand l'action attendue est réalisée. */
  advanceOn?: AdvanceOn;
}

export interface TourChapter {
  id: string;
  title: string;
  /** Résumé affiché sur la carte du menu de découverte. */
  description: string;
  /** Pictogramme (emoji) de la carte. */
  icon: string;
  /** Chapitre réservé à la direction (page/écran inaccessible sinon). */
  requiresDirection?: boolean;
  steps: TourStep[];
}

export const TOUR_CHAPTERS: TourChapter[] = [
  {
    id: 'prise-en-main',
    title: 'Prise en main',
    description: "Se repérer : la navigation, la barre du haut et le tableau de bord.",
    icon: '🧭',
    steps: [
      {
        title: 'Bienvenue dans votre espace de découverte',
        body:
          "Vous êtes dans un bac à sable qui n'appartient qu'à vous : créez, modifiez, " +
          'supprimez tout ce que vous voulez, rien n\'affecte les vraies données ni celles ' +
          'de vos collègues. Un jeu d\'exemples a été préparé pour explorer chaque écran. ' +
          'Cliquez sur « Suivant » pour commencer.',
      },
      {
        target: 'aside',
        route: '/',
        placement: 'right',
        title: 'La navigation',
        body:
          "À gauche, le menu regroupe les écrans de l'outil : Accueil, Notifications, " +
          'Projets, Actions, Tâches, Plan de charge, puis les Plans d\'actions et votre ' +
          'espace personnel. C\'est votre point de départ vers chaque module.',
      },
      {
        target: '[data-tour="topbar"]',
        placement: 'bottom',
        title: 'La barre du haut',
        body:
          'En haut à droite : la cloche des notifications (avec la pastille des nouveautés ' +
          'depuis votre dernière connexion), le thème clair / sombre, votre profil et la ' +
          'déconnexion.',
      },
      {
        target: '[data-tour="dashboard-grid"]',
        route: '/',
        placement: 'auto',
        title: 'Votre tableau de bord',
        body:
          'L\'accueil réunit quatre cadres : vos projets, vos actions, vos tâches et votre ' +
          'plan de charge. C\'est la vue d\'ensemble de tout ce qui vous concerne, en un ' +
          'coup d\'œil.',
      },
      {
        target: '[data-tour="dashboard-sort"]',
        placement: 'bottom',
        title: 'Trier ce qui compte',
        body:
          'Vous pouvez classer vos listes par priorité (du plus urgent au moins urgent) ou ' +
          'par échéance. Essayez de basculer entre les deux.',
        hint: 'Cliquez sur « Priorité » puis « Échéance » pour voir les listes se réordonner.',
      },
      {
        title: 'À vous de jouer',
        body:
          'Vous savez vous repérer. Depuis le menu « Découverte », choisissez le chapitre ' +
          'suivant quand vous voulez, ou explorez librement : votre espace vous attend. ' +
          'Pour tout remettre à neuf, utilisez « Réinitialiser » dans le bandeau de découverte.',
      },
    ],
  },
  // — Chapitres à venir (contenu rédigé en T-C) —
  {
    id: 'plans-actions',
    title: "Plans d'actions",
    description: 'Créer un plan, y ajouter des actions et suivre leur avancement (Module 1).',
    icon: '📋',
    steps: [
      {
        target: '[data-tour="plans-grid"]',
        route: '/plans',
        placement: 'auto',
        title: "Les plans d'actions",
        body:
          'Un plan d\'actions regroupe le travail d\'un client pour un exercice comptable. ' +
          'Chaque carte est un plan : son statut (actif / archivé), son budget et son avancement ' +
          'apparaissent en un coup d\'œil.',
      },
      {
        target: '[data-tour="plans-create"]',
        placement: 'bottom',
        title: 'Créer un plan',
        body:
          'La direction ouvre un nouveau plan pour un client et un exercice. Si ce bouton ne ' +
          's\'affiche pas chez vous, c\'est que la création est réservée à la direction — vous ' +
          'travaillez alors sur les plans existants.',
      },
      {
        target: '[data-tour="plans-card"]',
        placement: 'auto',
        title: 'Ouvrir un plan',
        body:
          'Un plan contient des projets et des actions. Ouvrez-en un pour découvrir son intérieur.',
        hint: 'Cliquez sur ce plan pour l\'ouvrir — la visite continue toute seule.',
        advanceOn: { type: 'routeChange' },
      },
      {
        target: '[data-tour="plan-detail-projects"]',
        placement: 'bottom',
        title: 'Les projets du plan',
        body:
          'En haut, les projets rattachés au plan. Chacun porte son équipe, ses échéances et son ' +
          'budget ; on les pilote en détail dans le module Gestion de projet.',
      },
      {
        target: '[data-tour="plan-detail-actions"]',
        placement: 'bottom',
        title: 'Les actions',
        body:
          'En dessous, les actions : les tâches concrètes à mener dans le cadre du plan, avec leur ' +
          'statut et leur avancement. C\'est le cœur du suivi opérationnel.',
      },
      {
        title: 'Le fil des plans',
        body:
          'Vous avez vu la chaîne : un plan → ses projets → ses actions. Les idées, elles, ' +
          'alimentent ce flux en amont — c\'est l\'objet du chapitre « Idéation ».',
      },
    ],
  },
  {
    id: 'ideation',
    title: 'Idéation',
    description: 'Déposer des idées, les faire mûrir et les transformer en actions.',
    icon: '💡',
    steps: [
      {
        target: '[data-tour="ideation-board"]',
        route: '/ideation',
        placement: 'auto',
        title: "L'idéation",
        body:
          'Un tableau pour faire émerger les idées avant qu\'elles ne deviennent des actions. ' +
          'Chaque colonne est une étape : le « Brainstorming », puis une colonne par plan actif.',
      },
      {
        target: '[data-tour="ideation-create"]',
        placement: 'bottom',
        title: 'Déposer une idée',
        body:
          'Tout le monde peut proposer une idée : un titre, une description, et elle rejoint le ' +
          'brainstorming. Essayez d\'en créer une.',
        hint: 'Cliquez sur « Nouvelle idée », remplissez et validez — la visite reprend ensuite.',
        advanceOn: { type: 'modalClosed' },
      },
      {
        target: '[data-tour="ideation-column"]',
        placement: 'right',
        title: 'Du brainstorming au plan',
        body:
          'Les idées naissent dans le « Brainstorming ». La direction les glisse ensuite dans un ' +
          'plan pour les classer, puis les transforme en actions — l\'idée devient alors un ' +
          'véritable travail suivi.',
      },
      {
        title: 'De l\'idée à l\'action',
        body:
          'Vous savez faire émerger et mûrir une idée. La suite — piloter concrètement le travail — ' +
          'se passe dans les projets, au chapitre suivant.',
      },
    ],
  },
  {
    id: 'gestion-projet',
    title: 'Gestion de projet',
    description: 'Piloter un projet : actions, tâches, membres et échéances (Module 2).',
    icon: '📁',
    steps: [
      {
        target: '[data-tour="projets-grid"]',
        route: '/projets',
        placement: 'auto',
        title: 'Vos projets',
        body:
          'Le cœur du Module 2. Les projets sont rangés en deux colonnes : ceux qui vous sont ' +
          'affectés et les autres. Chaque tuile résume l\'essentiel (client, échéance, équipe).',
      },
      {
        target: '[data-tour="projets-grid"]',
        placement: 'auto',
        title: 'Ouvrir un projet',
        body: 'Entrons dans un projet pour voir tout ce qu\'on peut y piloter.',
        hint: 'Cliquez sur un projet pour l\'ouvrir — la visite continue toute seule.',
        advanceOn: { type: 'routeChange' },
      },
      {
        target: '[data-tour="projet-header"]',
        placement: 'bottom',
        title: "L'en-tête du projet",
        body:
          'Le bandeau rassemble le nom, le client, les dates, la couleur et les membres du projet ' +
          '(sponsor, chef de projet, équipe).',
      },
      {
        target: '[data-tour="projet-tabs"]',
        placement: 'auto',
        title: 'Les facettes du projet',
        body:
          'Ces onglets offrent plusieurs regards sur le même travail : Actions & tâches, Kanban, ' +
          'Gantt, Réunions GT, Budget et Temps. On les explore dans les chapitres ' +
          'suivants.',
      },
      {
        target: '[data-tour="actions-liste"]',
        placement: 'auto',
        title: 'Actions & tâches',
        body:
          'La vue par défaut liste les actions du projet ; sous chacune, ses tâches. On y suit le ' +
          'statut et l\'avancement, on signale une action « à risque », et on ajoute une tâche ' +
          'd\'un mot.',
      },
      {
        title: 'Piloter, puis visualiser',
        body:
          'Vous savez lire un projet et son travail. Les vues visuelles — Kanban et Gantt — ' +
          'font l\'objet du chapitre suivant.',
      },
    ],
  },
  {
    id: 'kanban-gantt',
    title: 'Kanban & Gantt',
    description: 'Visualiser le travail : tableau Kanban, diagramme de Gantt.',
    icon: '📊',
    steps: [
      {
        target: '[data-tour="projets-grid"]',
        route: '/projets',
        placement: 'auto',
        title: 'Ouvrir un projet',
        body: 'Les vues visuelles vivent à l\'intérieur d\'un projet. Ouvrez-en un.',
        hint: 'Cliquez sur un projet pour l\'ouvrir — la visite continue toute seule.',
        advanceOn: { type: 'routeChange' },
      },
      {
        target: '[data-tour="projet-tabs"]',
        placement: 'bottom',
        title: 'Le Kanban',
        body:
          'Le même travail, vu autrement. Commençons par le tableau Kanban.',
        hint: 'Cliquez sur l\'onglet « Kanban ».',
        advanceOn: { type: 'appear', selector: '[data-tour="kanban"]' },
      },
      {
        target: '[data-tour="kanban"]',
        placement: 'auto',
        title: 'Glisser-déposer',
        body:
          'Les cartes se déplacent entre colonnes pour faire avancer les statuts, et la ' +
          'colonne « En retard » se remplit toute seule.',
      },
      {
        target: '[data-tour="projet-tabs"]',
        placement: 'bottom',
        title: 'Le Gantt',
        body: 'Passons à la frise temporelle.',
        hint: 'Cliquez sur l\'onglet « Gantt ».',
        advanceOn: { type: 'appear', selector: '[data-tour="gantt"]' },
      },
      {
        target: '[data-tour="gantt"]',
        placement: 'auto',
        title: 'Les échéances dans le temps',
        body:
          'Chaque action datée devient une barre positionnée selon son début et son échéance ; le ' +
          'repère « aujourd\'hui » situe le présent. Les retards ressortent en couleur.',
      },
      {
        title: 'Deux regards, un seul projet',
        body:
          'Kanban et Gantt : deux façons de lire le même travail. Ce qui est « à risque » ou en ' +
          'retard se signale sur les lignes elles-mêmes, et se retrouve dans le plan de charge. ' +
          'Reste la coordination humaine — les réunions — au chapitre suivant.',
      },
    ],
  },
  {
    id: 'reunions-gt',
    title: 'Réunions de groupe de travail',
    description: 'Planifier une réunion de GT, joindre le compte rendu PDF.',
    icon: '🗓️',
    steps: [
      {
        target: '[data-tour="projets-grid"]',
        route: '/projets',
        placement: 'auto',
        title: 'Ouvrir un projet',
        body: 'Les réunions se gèrent au sein d\'un projet. Ouvrez-en un.',
        hint: 'Cliquez sur un projet pour l\'ouvrir — la visite continue toute seule.',
        advanceOn: { type: 'routeChange' },
      },
      {
        target: '[data-tour="projet-tabs"]',
        placement: 'bottom',
        title: 'Les réunions du GT',
        body: 'Ouvrons l\'onglet des réunions du groupe de travail.',
        hint: 'Cliquez sur l\'onglet « Réunions GT ».',
        advanceOn: { type: 'appear', selector: '[data-tour="reunions"]' },
      },
      {
        target: '[data-tour="reunions"]',
        placement: 'auto',
        title: 'Planifier et rendre compte',
        body:
          'On planifie une réunion (le sponsor et le chef de projet en sont membres d\'office ; les ' +
          'participants reçoivent une invitation d\'agenda), puis on saisit le compte rendu et on ' +
          'joint son PDF. Tout l\'historique du GT reste ici.',
      },
      {
        title: 'La coordination tracée',
        body:
          'Réunions, comptes rendus et pièces jointes vivent dans le projet. Il reste à parler ' +
          'd\'argent et de temps — le chapitre suivant.',
      },
    ],
  },
  {
    id: 'budget-temps',
    title: 'Budget & temps',
    description: 'Estimer le temps des tâches et suivre la validation du budget.',
    icon: '💶',
    steps: [
      {
        target: '[data-tour="projets-grid"]',
        route: '/projets',
        placement: 'auto',
        title: 'Ouvrir un projet',
        body: 'Budget et temps se pilotent projet par projet. Ouvrez-en un.',
        hint: 'Cliquez sur un projet pour l\'ouvrir — la visite continue toute seule.',
        advanceOn: { type: 'routeChange' },
      },
      {
        target: '[data-tour="projet-tabs"]',
        placement: 'bottom',
        title: 'Le budget',
        body: 'Ouvrons d\'abord le budget du projet.',
        hint: 'Cliquez sur l\'onglet « Budget ».',
        advanceOn: { type: 'appear', selector: '[data-tour="budget"]' },
      },
      {
        target: '[data-tour="budget"]',
        placement: 'auto',
        title: 'Enveloppe, réel et écarts',
        body:
          'On y voit l\'enveloppe, le détail estimé / réel et les écarts, la consommation, et la ' +
          'validation du budget (réservée à la direction). Le bilan s\'exporte en PDF charté ITS.',
      },
      {
        target: '[data-tour="projet-tabs"]',
        placement: 'bottom',
        title: 'Le temps',
        body: 'Passons au suivi du temps.',
        hint: 'Cliquez sur l\'onglet « Temps ».',
        advanceOn: { type: 'appear', selector: '[data-tour="temps"]' },
      },
      {
        target: '[data-tour="temps"]',
        placement: 'auto',
        title: 'Temps estimé et valorisation',
        body:
          'Le temps estimé des tâches est totalisé et valorisé en euros (selon le coût horaire des ' +
          'fonctions). C\'est ce temps estimé qui alimente le plan de charge — le chapitre suivant.',
      },
      {
        title: 'De l\'estimation au plan de charge',
        body:
          'Vous savez lire le budget et le temps d\'un projet. Agrégés sur toute l\'équipe, ces ' +
          'temps forment le plan de charge, que nous verrons ensuite.',
      },
    ],
  },
  {
    id: 'plan-de-charge',
    title: 'Plan de charge',
    description: 'Lire la matrice de charge de l\'équipe et la vue individuelle (Module 3).',
    icon: '📈',
    steps: [
      {
        target: '[data-tour="plan-matrix"]',
        route: '/plan-de-charge',
        placement: 'auto',
        title: 'Le plan de charge',
        body:
          'La matrice croise les collaborateurs (en lignes) et les semaines (en colonnes). Chaque ' +
          'cellule additionne le temps estimé des tâches — c\'est la charge prévue, semaine par ' +
          'semaine.',
      },
      {
        target: '[data-tour="plan-mode-toggle"]',
        placement: 'bottom',
        title: 'Équipe ou individuel',
        body:
          'Deux angles de lecture : la vue « par collaborateur » (la matrice de toute l\'équipe) et ' +
          'la vue « individuel » (le détail jour par jour d\'une personne). Essayez de basculer.',
        hint: 'Cliquez « Individuel » pour voir le détail d\'une personne, puis revenez.',
      },
      {
        target: '[data-tour="plan-legend"]',
        placement: 'auto',
        title: 'Lire les couleurs',
        body:
          'La légende donne les seuils : disponible, charge normale, à surveiller, surcharge. On ' +
          'repère ainsi d\'un coup d\'œil qui est sous l\'eau et qui a de la marge.',
      },
      {
        title: 'Estimé aujourd\'hui, réel demain',
        body:
          'Le plan de charge s\'appuie pour l\'instant sur les temps estimés. Le temps réellement ' +
          'passé viendra l\'enrichir avec la redescente de la borne ITS.',
      },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Suivre les notifications et échanger directement depuis une alerte.',
    icon: '🔔',
    steps: [
      {
        target: '[data-tour="notif-tabs"]',
        route: '/notifications',
        placement: 'bottom',
        title: 'Vos notifications',
        body:
          'Tout ce qui vous concerne remonte ici : affectations, validations, relances, réunions. ' +
          'Les onglets séparent ce qui reste « À traiter » de ce que vous avez « Classé », et la ' +
          'cloche en haut compte les nouveautés depuis votre dernière connexion.',
      },
      {
        target: '[data-tour="notif-list"]',
        placement: 'auto',
        title: 'Lire, classer, échanger',
        body:
          'Les non-lues portent un liseré bleu : cliquer dessus suffit à les lire, il n\'y a rien ' +
          'à confirmer. Le seul geste qui reste est « Classer », qui la range dans l\'autre onglet. ' +
          'Quand elle vient d\'un collègue, « Conversation » ouvre le fil pour lui répondre — sans ' +
          'quitter la page — et le nombre affiché compte les messages déjà échangés.',
      },
      {
        title: 'Rien ne se perd',
        body:
          'Vous savez lire, classer et prolonger vos notifications. Passons à votre coin personnel dans ' +
          'l\'outil.',
      },
    ],
  },
  {
    id: 'mon-espace',
    title: 'Mon espace',
    description: 'Tâches personnelles (liste, Kanban, calendrier) et pense-bête.',
    icon: '🗂️',
    steps: [
      {
        target: '[data-tour="espace-tabs"]',
        route: '/mon-espace',
        placement: 'auto',
        title: 'Mon espace',
        body:
          'Votre coin à vous : vos tâches personnelles et un pense-bête. Rien ici n\'est imposé ' +
          'par un projet — c\'est votre organisation propre.',
      },
      {
        target: '[data-tour="espace-task-views"]',
        placement: 'bottom',
        title: 'Trois façons de voir vos tâches',
        body:
          'Vos tâches personnelles s\'affichent en Liste, en Kanban ou en Calendrier (semaine de ' +
          'travail). Choisissez la vue qui vous parle. Essayez de basculer.',
        hint: 'Cliquez « Kanban » puis « Calendrier » pour comparer.',
      },
      {
        target: '[data-tour="espace-new-task"]',
        placement: 'bottom',
        title: 'Créer une tâche perso',
        body: 'Ajoutez une tâche pour vous — un titre suffit, les détails sont optionnels.',
        hint: 'Cliquez « Nouvelle tâche », créez-en une — la visite reprend ensuite.',
        advanceOn: { type: 'modalClosed' },
      },
      {
        target: '[data-tour="espace-tabs"]',
        placement: 'bottom',
        title: 'Le pense-bête',
        body: 'Il reste le pense-bête : ouvrons-le.',
        hint: 'Cliquez sur l\'onglet « Pense-bête ».',
        advanceOn: { type: 'appear', selector: '[data-tour="espace-memo"]' },
      },
      {
        target: '[data-tour="espace-memo"]',
        placement: 'auto',
        title: 'Noter, rattacher, transformer',
        body:
          'Le pense-bête range vos notes par thématique. Cliquez sur l\'une d\'elles : sa fiche ' +
          's\'ouvre à droite — le détail, le projet et l\'action concernés, l\'échéance. La liste ' +
          'se filtre sur ces mêmes rattachements. Et une note qui prend de l\'ampleur peut être ' +
          'transformée en idée, pour rejoindre le circuit de l\'idéation.',
      },
      {
        title: 'Votre espace, votre rythme',
        body:
          'Tâches perso et pense-bête vous suivent partout. Il reste un écran, réservé à la ' +
          'direction : l\'administration.',
      },
    ],
  },
  {
    id: 'administration',
    title: 'Administration',
    description: 'Attribuer les rôles et gérer les capacités (réservé à la direction).',
    icon: '⚙️',
    requiresDirection: true,
    steps: [
      {
        target: '[data-tour="admin-tabs"]',
        route: '/administration',
        placement: 'auto',
        title: "L'administration",
        body:
          'Réservé à la direction. On y règle les référentiels de l\'outil : les fonctions et leurs ' +
          'coûts horaires, les libellés de statuts, les catégories de dépense, et les comptes ' +
          'collaborateurs.',
      },
      {
        target: '[data-tour="admin-tabs"]',
        placement: 'bottom',
        title: 'Les collaborateurs',
        body: 'Ouvrons la gestion des comptes.',
        hint: 'Cliquez sur l\'onglet « Collaborateurs ».',
        advanceOn: { type: 'appear', selector: '[data-tour="admin-users-table"]' },
      },
      {
        target: '[data-tour="admin-users-table"]',
        placement: 'auto',
        title: 'Rôles et capacité',
        body:
          'Pour chaque collaborateur : ses rôles (dont Testeur et Comex), sa fonction (qui fixe son ' +
          'coût horaire) et sa capacité hebdomadaire — cette dernière sert de base au plan de ' +
          'charge.',
      },
      {
        title: 'Les réglages en un endroit',
        body:
          'Vous avez fait le tour de l\'outil ! Vous pouvez rejouer n\'importe quel chapitre, ou ' +
          'quitter la découverte pour retrouver vos vraies données quand vous voulez.',
      },
    ],
  },
];

export function chapterById(id: string): TourChapter | null {
  return TOUR_CHAPTERS.find((c) => c.id === id) ?? null;
}
